'use client';

import { useEffect } from 'react';
import { useRouter, usePathname } from 'next/navigation';
import { useSelector } from 'react-redux';
import { RootState } from '@/lib/store';

import { Roles } from '@/types/auth';

export default function AuthGuard({ children }: { children: React.ReactNode }) {
    const { token, user } = useSelector((state: RootState) => state.auth);
    const isAuthenticated = !!token;
    const router = useRouter();
    const pathname = usePathname();

    useEffect(() => {
        const isPublicRoute = pathname === '/';

        if (!isAuthenticated && !isPublicRoute) {
            router.push('/');
        } else if (isAuthenticated && isPublicRoute) {
            // Role-based redirection logic
            const userRole = (user?.groups && user.groups[0]) || '';

            const roleConfig: Record<string, string> = {
                [Roles.CSO]: "/cso/dashboard",
                [Roles.EVALUATOR]: "/evaluator/dashboard",
                [Roles.SUPERVISOR]: "/supervisor/dashboard",
                [Roles.MALL_MANAGER]: "/mall-manager/dashboard",
                [Roles.QA]: "/qa/dashboard",
                [Roles.CS]: "/cs-manager/dashboard",
                // Roles.SUPER_ADMIN -> likely needs a path, for now default or specific
                [Roles.SUPER_ADMIN]: "/dashboard",
            };

            const targetPath = roleConfig[userRole];

            if (targetPath) {
                router.push(targetPath);
            } else {
                console.error('AuthGuard: Unknown user role', userRole);
                // Stay on login page but show error state (handled by UI ideally)
            }
        }
    }, [isAuthenticated, pathname, router, user]);

    return <>{children}</>;
}
